// The one waiting + error wrapper every module gets in live mode (bridge.tsx wraps the
// widget in it only with ?live=1). While a live hook's answer is on the way →
// <LiveLoading/>; if the call fails → <LiveError/> with Retry. The widget's own code never
// sees either — it only ever renders with its data, as on the snapshot site.

import { Component, Suspense, type ReactNode } from "react";
import { LiveError, LiveLoading, LiveReveal } from "./LoadState.js";
import { LiveModuleContext, forgetLiveAnswers } from "./query.js";

class LiveErrorBoundary extends Component<{ mod: HTMLElement; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  // Fail loud (§12.2): the widget shows Retry, and the reason still reaches the console.
  componentDidCatch(error: unknown): void {
    console.error("[live] a widget's data failed to load:", error);
  }

  private retry = () => {
    forgetLiveAnswers(this.props.mod);
    this.setState({ failed: false });
  };

  render() {
    return this.state.failed ? <LiveError onRetry={this.retry} /> : this.props.children;
  }
}

/**
 * One part of a widget that may fail on its own while the rest stays usable (Ben, 2026-10-26: a
 * Matches season that failed hid the season list, so there was no way to pick another). The
 * fallback gets a Retry. Give it a `key` (e.g. the season) so picking something else tries afresh.
 */
export class LivePartBoundary extends Component<
  { mod: HTMLElement; fallback: (retry: () => void) => ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    console.error("[live] part of a widget failed to load:", error);
  }

  private retry = () => {
    forgetLiveAnswers(this.props.mod);
    this.setState({ failed: false });
  };

  render() {
    return this.state.failed ? this.props.fallback(this.retry) : this.props.children;
  }
}

export function LiveBoundary({ mod, children }: { mod: HTMLElement; children: ReactNode }) {
  return (
    <LiveModuleContext value={mod}>
      <LiveErrorBoundary mod={mod}>
        <Suspense fallback={<LiveLoading />}>
          <LiveReveal mod={mod} />
          {children}
        </Suspense>
      </LiveErrorBoundary>
    </LiveModuleContext>
  );
}
